import { MutationTuple, QueryResult, useMutation, useQuery } from '@apollo/client'
import { ApolloCache, DocumentNode, FetchResult, OperationVariables, TypedDocumentNode } from '@apollo/client/core'
import { MutationFunctionOptions, MutationHookOptions, QueryHookOptions } from '@apollo/client/react/types/types'
import { useIsAuthenticated } from '@azure/msal-react'
import { CircularProgress } from '@mui/material'
import React from 'react'

type CustomQueryResult<TD, TV extends OperationVariables> = Omit<QueryResult<TD, TV>, 'data'> & {
  data: TD | Record<string, any>
}

type QueryProps<TD, TV extends OperationVariables> = {
  query: DocumentNode | TypedDocumentNode<TD, TV>
  children: (result: CustomQueryResult<TD, TV>) => JSX.Element
} & QueryHookOptions<TD, TV>

export type MutationFunction<
  TD = unknown,
  TV = unknown,
  TContext = unknown,
  TCache extends ApolloCache<any> = ApolloCache<any>,
> = (options?: MutationFunctionOptions<TD, TV, TContext, TCache> | undefined) => Promise<FetchResult<TD>>

export const Query = <TD, TV extends OperationVariables>(props: QueryProps<TD, TV>) => {
  const { query, children, ...options } = props
  const isAuthenticated = useIsAuthenticated()
  const {
    data = {},
    refetch,
    error,
    ...result
  } = useQuery(query, {
    ...options,
    errorPolicy: 'all',
    skip: !isAuthenticated,
  })
  if (error) {
    return <CircularProgress />
  }
  return children({ data, ...result, refetch })
}

type MutationProps<TD, TV, TContext, TCache extends ApolloCache<any> = ApolloCache<any>> = {
  mutation: DocumentNode | TypedDocumentNode<TD, TV>
  children: (
    result: MutationTuple<TD, TV, TContext, TCache>[0],
    options: MutationTuple<TD, TV, TContext, TCache>[1],
  ) => JSX.Element
} & MutationHookOptions<TD, TV, TContext>

export const Mutation = <TD, TV, TContext, TCache extends ApolloCache<any> = ApolloCache<any>>(
  props: MutationProps<TD, TV, TContext, TCache>,
) => {
  const { mutation, children, ...options } = props
  const [mutationFunction, mutationResult] = useMutation<TD, TV, TContext, TCache>(mutation, options)
  return children(mutationFunction, mutationResult)
}
